import { useEffect, useState, type ReactNode } from 'react';

import { IDLE_TODAY_EMPTY_HOST, type TodayEmptyHost } from './useTodayAnyway';

// The host's side of the empty «today» card: its coverage loads only while
// such a draft is open and empty, not every time the Workouts screen mounts.
// `useHost` is the platform's hook (a module-level function, so its identity
// never changes).

// Mounted only while the draft is empty. Renders nothing; reports up.
function TodayEmptyProbe({
  useHost,
  onHost,
}: {
  useHost: () => TodayEmptyHost;
  onHost: (host: TodayEmptyHost) => void;
}) {
  const { onBuildAnyway, weeklyMinimumMet } = useHost();
  useEffect(() => {
    onHost({ onBuildAnyway, weeklyMinimumMet });
  }, [onBuildAnyway, weeklyMinimumMet, onHost]);
  useEffect(() => () => onHost(IDLE_TODAY_EMPTY_HOST), [onHost]);
  return null;
}

/** `probe` goes in the tree before the LoggerScreen; `host` and `onEmptyChange`
 *  go to its props. */
export function useTodayEmptyState(useHost: () => TodayEmptyHost): {
  probe: ReactNode;
  host: TodayEmptyHost;
  onEmptyChange: (empty: boolean) => void;
} {
  const [open, setOpen] = useState(false);
  const [host, setHost] = useState<TodayEmptyHost>(IDLE_TODAY_EMPTY_HOST);
  return {
    probe: open ? <TodayEmptyProbe useHost={useHost} onHost={setHost} /> : null,
    host,
    onEmptyChange: setOpen,
  };
}
